import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// Helper function to calculate semantic similarity between two strings
const calculateSimilarity = (str1, str2) => {
  // Simple word-based similarity calculation
  if (!str1 || !str2) return 0;

  // Convert to lowercase and split into words
  const words1 = str1
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2);
  const words2 = str2
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2);

  // Find common words
  const commonWords = words1.filter((word) => words2.includes(word));

  // Calculate similarity score (Jaccard similarity)
  const uniqueWords = [...new Set([...words1, ...words2])];
  return uniqueWords.length ? commonWords.length / uniqueWords.length : 0;
};

const BillComparisonView = ({ bills, poId, compareBill }) => {
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragGroups, setDragGroups] = useState([]);
  const [usingSemanticSort, setUsingSemanticSort] = useState(true);
  const [billSummaries, setBillSummaries] = useState({});
  const [showBillSummaries, setShowBillSummaries] = useState(false);

  useEffect(() => {
    if (!bills || bills.length === 0) {
      setLoading(false);
      return;
    }

    // Process all bills to create item groups (main item + deductions)
    const allRows = [];
    let rowId = 0;
    let dragGroupId = 0;
    const initialGroupMap = {}; // Maps description to group ID for semantic matching
    const allGroups = []; // All group objects with their main items for sorting
    const summaries = {}; // Collect bill summaries

    bills.forEach((bill) => {
      // Initialize summary for this bill
      const billNo = bill.billNo;
      summaries[billNo] = {
        claimedQuantity: 0,
        approvedQuantity: 0,
        totalAmount: 0,
      };

      // Extract approved and claimed quantities from workOrderItems directly
      // (outside of the items property)
      if (bill.workOrderItems) {
        Object.values(bill.workOrderItems).forEach((section) => {
          // Add approved and claimed quantities from the section level
          if (section.approvedQuantity) {
            summaries[billNo].approvedQuantity +=
              parseFloat(section.approvedQuantity) || 0;
          }
          if (section.claimedQuantity) {
            summaries[billNo].claimedQuantity +=
              parseFloat(section.claimedQuantity) || 0;
          }
        });
      }

      // Process both workOrderItems and additionalItems for total amount calculation
      [bill.workOrderItems, bill.additionalItems].forEach((itemSection) => {
        if (!itemSection) return;

        Object.entries(itemSection).forEach(([heading, section]) => {
          if (!section.items) return;

          Object.entries(section.items).forEach(([itemId, item]) => {
            const description = item.description;
            if (!description) return;

            // Add item amount to total amount
            if (item.amount) {
              summaries[billNo].totalAmount += parseFloat(item.amount) || 0;
            }

            // Generate a unique group ID
            const currentDragGroupId = `drag-group-${dragGroupId++}`;

            // Check if there's a semantically similar item already
            let foundSimilarGroup = false;

            if (usingSemanticSort) {
              // Check for semantic similarity with existing groups
              Object.entries(initialGroupMap).forEach(
                ([existingDesc, existingGroupId]) => {
                  const similarity = calculateSimilarity(
                    description,
                    existingDesc
                  );
                  if (similarity > 0.6) {
                    // Threshold for similarity
                    foundSimilarGroup = existingGroupId;
                    return;
                  }
                }
              );
            }

            // Use existing group or create new one
            const groupId = foundSimilarGroup || currentDragGroupId;

            if (!foundSimilarGroup) {
              initialGroupMap[description] = groupId;
              allGroups.push({
                groupId,
                description,
                heading: section.category || "",
              });
            }

            // Create row for the main item
            const mainRow = {
              id: `row-${rowId++}`,
              dragGroupId: groupId,
              heading: section.category || "",
              billNo: bill.billNo,
              description,
              isDeduction: false,
              units: item.units || "-",
              nos: item.nos || 0,
              length: item.dimensions?.length || 0,
              breadth: item.dimensions?.breadth || 0,
              height: item.dimensions?.height || 0,
              totalQuantity: item.totalQuantity || 0,
              rate: item.rate || 0,
              amount: item.amount || 0,
            };

            allRows.push(mainRow);

            // Process deductions
            if (item.deductions) {
              Object.values(item.deductions).forEach((deduction) => {
                if (!deduction.description) return;

                // Subtract deduction amount from total amount
                if (deduction.amount) {
                  summaries[billNo].totalAmount -=
                    parseFloat(deduction.amount) || 0;
                }

                const deductionRow = {
                  id: `row-${rowId++}`,
                  dragGroupId: groupId, // Same group as parent item
                  heading: section.category || "",
                  billNo: bill.billNo,
                  description: `• ${deduction.description || "Deduction"}`,
                  isDeduction: true,
                  units: deduction.units || "-",
                  nos: deduction.nos || 0,
                  length: deduction.dimensions?.length || 0,
                  breadth: deduction.dimensions?.breadth || 0,
                  height: deduction.dimensions?.height || 0,
                  totalQuantity: deduction.totalQuantity || 0,
                  rate: deduction.rate || 0,
                  amount: deduction.amount || 0,
                };

                allRows.push(deductionRow);
              });
            }
          });
        });
      });
    });

    // Save the summaries
    setBillSummaries(summaries);
    setTableRows(allRows);

    // Sort groups by heading and then by description for initial semantic order
    const sortedGroups = allGroups.sort((a, b) => {
      // First sort by heading
      if (a.heading < b.heading) return -1;
      if (a.heading > b.heading) return 1;

      // Then sort by description
      return a.description.localeCompare(b.description);
    });

    const initialDragGroups = sortedGroups.map((group) => group.groupId);

    // Load from cache or use initial semantic order
    const cachedOrderData = localStorage.getItem(`dragGroups-${poId}`);
    if (cachedOrderData) {
      try {
        const parsedData = JSON.parse(cachedOrderData);
        const { order, timestamp } = parsedData;

        // Check if cache is valid (contains all current groups)
        const allGroupsPresent = initialDragGroups.every((group) =>
          order.includes(group)
        );

        // If cache is valid and not too old, use it - otherwise use semantic order
        if (allGroupsPresent) {
          setDragGroups(order);

          // If it's been more than a day since last change, reapply semantic sort
          const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
          if (Date.now() - timestamp > oneDay) {
            setUsingSemanticSort(true);
            setDragGroups(initialDragGroups);
            saveDragOrderToCache(initialDragGroups);
          } else {
            setUsingSemanticSort(false);
          }

          setLoading(false);
          return;
        }
      } catch (e) {
        console.error("Error parsing cached drag groups:", e);
      }
    }

    // Use semantic order if no cache or cache is invalid
    setDragGroups(initialDragGroups);
    saveDragOrderToCache(initialDragGroups);
    setLoading(false);
  }, [bills, poId, usingSemanticSort]);

  // Helper function to save drag order to cache with timestamp
  const saveDragOrderToCache = (order) => {
    const dataToSave = {
      order,
      timestamp: Date.now(),
    };
    localStorage.setItem(`dragGroups-${poId}`, JSON.stringify(dataToSave));
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === 0) return "0.00";
    return parseFloat(value).toFixed(2);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return "₹ 0.00";
    return `₹ ${parseFloat(value).toFixed(2)}`;
  };

  // Handle drag end for row reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragGroups);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setDragGroups(items);
    setUsingSemanticSort(false); // User has manually changed the order

    // Save the exact order in cache, preserving user-defined order
    saveDragOrderToCache(items);
  };

  // Function to reset to semantic sorting
  const resetToSemanticSort = () => {
    setUsingSemanticSort(true);
  };

  if (loading) {
    return (
      <div className="comparison-loading">
        <div className="loader"></div>
        <p>Loading comparison data...</p>
      </div>
    );
  }

  if (tableRows.length === 0) {
    return (
      <div className="comparison-no-data">
        <p>No comparable items found across bills.</p>
      </div>
    );
  }

  return (
    <div className="bill-comparison-container">
      {compareBill && (
        <h2 className="comparison-title">Bill Comparison for PO #{poId}</h2>
      )}

      {compareBill && (
        <div className="comparison-instructions">
          <p>
            Drag items to reorder. Items and their deductions will move together
            for easier comparison.
          </p>
          {!usingSemanticSort && (
            <button
              className="reset-semantic-sort"
              onClick={resetToSemanticSort}
            >
              Reset to semantic grouping
            </button>
          )}
        </div>
      )}

      {/* Bill Summary Section with collapsable functionality */}
      <div className="bill-summaries-section">
        <div
          className="summary-header"
          onClick={() => setShowBillSummaries(!showBillSummaries)}
        >
          <h3>Bill Summaries</h3>
          <span
            className={`toggle-icon ${
              showBillSummaries ? "expanded" : "collapsed"
            }`}
          >
            {showBillSummaries ? (
              <KeyboardArrowDownIcon />
            ) : (
              <KeyboardArrowRightIcon />
            )}
          </span>
        </div>

        {showBillSummaries && (
          <div className="bill-summaries-container">
            {Object.entries(billSummaries).map(([billNo, summary]) => (
              <div key={billNo} className="bill-summary-row">
                <div className="bill-number">{billNo}</div>
                <div className="claimed-quantity">
                  Claimed Qty : {formatNumber(summary.claimedQuantity)}
                </div>
                <div className="approved-quantity">
                  Approved Qty : {formatNumber(summary.approvedQuantity)}
                </div>
                <div className="amount">
                  Amount : ₹ {formatNumber(summary.totalAmount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {compareBill && (
        <div className="table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Heading
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Bill No
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Description
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Units
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Nos
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Length
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Breadth
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Height
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Total Quantity
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Rate (₹)
                </th>
                <th style={{ textAlign: "center", backgroundColor: "#FFEEBA" }}>
                  Amount (₹)
                </th>
              </tr>
            </thead>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="bill-items">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {dragGroups.map((groupId, groupIndex) => {
                      // Get rows for this group and sort (main item first, then deductions)
                      const groupRows = tableRows
                        .filter((row) => row.dragGroupId === groupId)
                        .sort((a, b) =>
                          a.isDeduction === b.isDeduction
                            ? 0
                            : a.isDeduction
                            ? 1
                            : -1
                        );

                      if (groupRows.length === 0) return null;

                      // The main item is always the first in the group
                      const mainItem = groupRows[0];
                      const deductions = groupRows.slice(1);

                      return (
                        <Draggable
                          key={groupId}
                          draggableId={groupId}
                          index={groupIndex}
                        >
                          {(provided, snapshot) => (
                            <>
                              {/* Main item row */}
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`item-row ${
                                  snapshot.isDragging ? "dragging" : ""
                                } ${
                                  deductions.length > 0 ? "has-deductions" : ""
                                }`}
                              >
                                <td>{mainItem.heading}</td>
                                <td>{mainItem.billNo}</td>
                                <td>
                                  {mainItem.description}
                                  {deductions.length > 0 && (
                                    <span className="deduction-badge">
                                      +{deductions.length} deduction
                                      {deductions.length > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </td>
                                <td>{mainItem.units}</td>
                                <td>{mainItem.nos}</td>
                                <td>{formatNumber(mainItem.length)}</td>
                                <td>{formatNumber(mainItem.breadth)}</td>
                                <td>{formatNumber(mainItem.height)}</td>
                                <td className="quantity-cell">
                                  {formatNumber(mainItem.totalQuantity)}
                                </td>
                                <td>{formatNumber(mainItem.rate)}</td>
                                <td className="amount-cell">
                                  {formatCurrency(mainItem.amount)}
                                </td>
                              </tr>

                              {/* Deduction rows */}
                              {deductions.map((deduction) => (
                                <tr
                                  key={deduction.id}
                                  className="deduction-row"
                                >
                                  <td></td>
                                  <td>{deduction.billNo}</td>
                                  <td className="deduction-description">
                                    {deduction.description}
                                  </td>
                                  <td>{deduction.units}</td>
                                  <td>{deduction.nos}</td>
                                  <td>{formatNumber(deduction.length)}</td>
                                  <td>{formatNumber(deduction.breadth)}</td>
                                  <td>{formatNumber(deduction.height)}</td>
                                  <td className="deduction-quantity">
                                    {formatNumber(deduction.totalQuantity)}
                                  </td>
                                  <td>{formatNumber(deduction.rate)}</td>
                                  <td className="deduction-amount">
                                    -{formatCurrency(deduction.amount)}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      )}

      <style jsx>{`
        .bill-comparison-container {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            Oxygen, Ubuntu, Cantarell, sans-serif;
          max-width: 100%;
          background-color: #fff;
          border-radius: 8px;
        }

        .comparison-title {
          color: #333;
          margin-bottom: 16px;
          font-size: 20px;
          font-weight: 600;
        }

        .comparison-instructions {
          background-color: #f5f7fa;
          border-left: 3px solid #4a6cf7;
          padding: 12px 16px;
          margin-bottom: 16px;
          font-size: 14px;
          color: #555;
          border-radius: 0 4px 4px 0;
        }

        .table-container {
          width: 100%;
          overflow: visible;
          border-radius: 4px;
          max-height: unset;
          display: block;
        }

        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          table-layout: fixed;
        }

        .comparison-table th,
        .comparison-table td {
          overflow: visible;
          text-overflow: ellipsis;
          white-space: normal;
          height: auto;
          word-wrap: break-word;
          vertical-align: top;
        }

        /* Set fixed widths for each column */
        .comparison-table th:nth-child(1),
        .comparison-table td:nth-child(1) {
          width: 12%;
          white-space: normal;
          word-break: break-word;
        }
        .comparison-table th:nth-child(2),
        .comparison-table td:nth-child(2) {
          width: 8%;
        }
        .comparison-table th:nth-child(3),
        .comparison-table td:nth-child(3) {
          width: 18%;
          white-space: normal;
          word-break: break-word;
        }
        .comparison-table th:nth-child(4),
        .comparison-table td:nth-child(4) {
          width: 7%;
        }
        .comparison-table th:nth-child(5),
        .comparison-table td:nth-child(5) {
          width: 5%;
        }
        .comparison-table th:nth-child(6),
        .comparison-table td:nth-child(6) {
          width: 7%;
        }
        .comparison-table th:nth-child(7),
        .comparison-table td:nth-child(7) {
          width: 7%;
        }
        .comparison-table th:nth-child(8),
        .comparison-table td:nth-child(8) {
          width: 7%;
        }
        .comparison-table th:nth-child(9),
        .comparison-table td:nth-child(9) {
          width: 10%;
        }
        .comparison-table th:nth-child(10),
        .comparison-table td:nth-child(10) {
          width: 7%;
        }
        .comparison-table th:nth-child(11),
        .comparison-table td:nth-child(11) {
          width: 10%;
        }

        .comparison-table th {
          background-color: #ffeeba !important;
          color: #495057;
          padding: 10px 12px;
          text-align: center;
          font-weight: 600;
          border-bottom: 2px solid #e4e7eb;
          white-space: nowrap;
        }

        .comparison-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #e4e7eb;
          vertical-align: middle;
          white-space: nowrap;
        }

        .comparison-table td:not(:first-child) {
          border-left: 1px solid #f0f0f0;
        }

        .item-row {
          background-color: #ffffff;
          cursor: grab;
        }

        .item-row:hover {
          background-color: #f8f9fa;
        }

        .dragging {
          background-color: #f0f7ff !important;
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
        }

        .deduction-row {
          background-color: #fff9f9;
        }

        .deduction-description {
          color: #e53e3e;
          font-size: 13px;
          padding-left: 12px;
        }

        .quantity-cell {
          color: #3182ce;
          font-weight: 500;
        }

        .deduction-quantity {
          color: #e53e3e;
        }

        .amount-cell {
          color: #2f855a;
          font-weight: 500;
        }

        .deduction-amount {
          color: #e53e3e;
          font-weight: 500;
        }

        .loader {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #4a6cf7;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        .comparison-loading {
          text-align: center;
          padding: 32px;
          color: #666;
        }

        .comparison-no-data {
          text-align: center;
          padding: 32px;
          color: #666;
          background-color: #f9fafb;
          border-radius: 4px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .deduction-badge {
          display: inline-block;
          background-color: #fed7d7;
          color: #e53e3e;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 12px;
          margin-left: 8px;
          font-weight: 600;
          vertical-align: middle;
        }

        .has-deductions {
          border-left: 3px solid #e53e3e;
        }

        .has-deductions.dragging {
          background-color: #f0f7ff !important;
          box-shadow: 0 0 12px rgba(0, 0, 0, 0.15);
          border-left: 3px solid #e53e3e;
        }

        .dragging .deduction-badge {
          background-color: #e53e3e;
          color: white;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.7;
          }
        }

        .reset-semantic-sort {
          background-color: #4a6cf7;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          margin-top: 8px;
          transition: background-color 0.2s;
        }

        .reset-semantic-sort:hover {
          background-color: #3a5ce5;
        }

        /* Bill Summary Styling */
        .bill-summaries-container {
          margin-bottom: 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .bill-summary-row {
          display: flex;
          background-color: #ffeeba;
          padding: 10px 16px;
          border-radius: 4px;
          align-items: center;
          font-size: 14px;
        }

        .bill-number {
          flex: 1;
          text-align: left;
          font-weight: 600;
          color: #333;
        }

        .claimed-quantity {
          flex: 1;
          font-weight: 600;
          color: #333;

          text-align: center;
        }

        .approved-quantity {
          flex: 1;
          font-weight: 600;
          color: #2f855a;
          text-align: center;
        }

        .amount {
          flex: 1;
          text-align: right;
          font-weight: 600;
          color: #2f855a;
        }

        .bill-summaries-section {
          margin-bottom: 24px;
        }

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          background-color: #f5f7fa;
          padding: 10px 16px;
          border-radius: 4px;
          border-left: 3px solid #4a6cf7;
          transition: background-color 0.2s ease;
        }

        .summary-header:hover {
          background-color: #edf2fa;
        }

        .summary-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .toggle-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #4a6cf7;
        }

        .toggle-icon svg {
          font-size: 24px;
          transition: transform 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default BillComparisonView;
